import Avatar from "../components/Avatar";
import { RatingIcon } from "../components/RatingIcon";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { BaseLivlyApiResponse } from "../types/Base";
import { BASE_API_URL } from "../utils/constants";
import { Spinner } from "../components/Spinner";
import toLocalTime from "../utils/toLocalTime";
import useLivlyUser from "../context/UserProvider";
import { getIsFeedbackTabsVisible } from "./feedback";
import { Link, Navigate, Outlet } from "react-router";
import moment from "moment-timezone";
import { Button } from "@/components/Button";
import useIsAppFolioMaintenanceProperty from "@/hooks/useIsAppFolioMaintenanceProperty";
import { FeedbackType } from "./feedback-external/context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Review {
  id: number;
  title: string;
  timestamp: string;
  text: string;
  rating: number;
  feedbackItems: ReviewFeedbackItem[];
  replies: ReviewReply[];
  feedbackType: FeedbackType | undefined;
}

interface ReviewFeedbackItem {
  id: number;
  description: string;
  type: number;
  name: string;
}

interface ReviewReply {
  firstName: string;
  lastName: string;
  avatarUrl: string;
  timestamp: string;
  text: string;
  reviewRequested: boolean;
  reviewLocations: ReviewLocation[];
}

interface ReviewLocation {
  type: number;
  name: string;
  url: string;
}

type FeedbackEntry = Review & {
  entryType?: string;
  surveyId?: number;
  surveyStatus?: string | number | null;
  expiresAt?: string | null;
};

async function getReviews(leaseId: number) {
  const result = await axios.get<BaseLivlyApiResponse<FeedbackEntry[]>>(
    `${BASE_API_URL}/resident/feedback?leaseId=${leaseId}`
  );

  return result.data.Data;
}

const feedbackQuery = (leaseId: number) => ({
  queryKey: ["reviews", leaseId],
  queryFn: () => getReviews(leaseId),
  refetchOnWindowFocus: false,
});

export default function FeedbackAndReviewsPage() {
  const { user } = useLivlyUser();
  const { data, isLoading } = useQuery({
    ...feedbackQuery(user.propertyUnitLeaseId),
  });
  const entries = (data ?? []) as FeedbackEntry[];
  const { pollsEnabled, feedbackEnabled } = getIsFeedbackTabsVisible(
    user.building.serviceTypes ?? []
  );

  if (pollsEnabled && !feedbackEnabled) {
    return <Navigate to="../polls" replace />;
  }
  const hideCategory = useIsAppFolioMaintenanceProperty();

  // Filter only reviews (exclude surveys)
  const filteredEntries = entries.filter((entry) => {
    const isSurvey = String(entry.entryType ?? "").toLowerCase() === "survey";
    return !isSurvey;
  });

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center flex-1 my-56">
          <Spinner color="livly" size="xl" />
        </div>
      ) : (
        <div>
          {filteredEntries.length === 0 ? (
            <div className="my-10 text-center">
              <h4 className="text-xl">No reviews yet</h4>
              <p className="mt-2 text-sm font-light">
                Any feedback or reviews you leave will appear here
              </p>
            </div>
          ) : null}

          {filteredEntries.length > 0 && (
            <ul className="text-sm divide-y divide-gray-200">
              {filteredEntries.map((entry) => {
                const isEditAvailable = moment().diff(entry.timestamp, "days") < 7;

                return (
                  <li key={entry.id} className="py-4">
                    <div className="flex justify-between gap-4">
                      <div>
                        <h3 className="font-medium">
                          {hideCategory && entry.feedbackType === "MaintenanceTicket"
                            ? "Maintenance Ticket"
                            : entry.title}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {toLocalTime(entry.timestamp).fromNow()}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {isEditAvailable && (
                          <Link to={`edit/${entry.id}`} state={{ background: entry }}>
                            <Button size="small" color="default">
                              Edit
                            </Button>
                          </Link>
                        )}
                        <RatingIcon rating={entry.rating} isSelected size="2x" />
                      </div>
                    </div>
                    {entry.feedbackItems.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-4">
                        {entry.feedbackItems.map((item) => (
                          <div
                            key={item.type}
                            className="px-1 text-xs text-blue-600 border border-blue-400 rounded-md bg-blue-50"
                          >
                            {item.description}
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="mt-4 text-sm">{entry.text}</p>
                    {entry.replies.length > 0 && (
                      <ul className="mt-4">
                        {entry.replies.map((reply) => (
                          <li key={reply.timestamp} className="p-2 bg-gray-100 rounded-md">
                            <div className="flex gap-4">
                              <div className="flex-shrink-0">
                                <Avatar
                                  src={reply.avatarUrl}
                                  name={`${reply.firstName} ${reply.lastName}`}
                                  size="xs"
                                />
                              </div>
                              <div>
                                <span>
                                  <strong>
                                    {reply.firstName} {reply.lastName}
                                  </strong>{" "}
                                  <span className="text-xs text-gray-500">
                                    {toLocalTime(reply.timestamp).fromNow()}
                                  </span>
                                </span>
                                <p className="mt-2">{reply.text}</p>
                                {reply.reviewRequested && (
                                  <ul className="mt-2 ">
                                    {reply.reviewLocations.map((location) => (
                                      <li key={location.type} className="py-4">
                                        <a
                                          href={location.url}
                                          target="_blank"
                                          className="p-2 text-sm text-red-400 rounded-md hover:bg-gray-200"
                                        >
                                          Leave the review on {location.name}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <Outlet />
    </>
  );
}
